// Rome is the business clock, UTC the database's (FLBI's dual clock): the list shows one of them, to the millisecond.
export type Zone = 'rome' | 'utc';

const TIME_ZONE: Readonly<Record<Zone, string>> = { rome: 'Europe/Rome', utc: 'UTC' };
const clockFormat = (zone: Zone): Intl.DateTimeFormat =>
    new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE[zone], hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
const CLOCK: Readonly<Record<Zone, Intl.DateTimeFormat>> = { rome: clockFormat('rome'), utc: clockFormat('utc') };
// en-CA writes 2026-10-14: a day's key that sorts.
const DAY_KEY: Readonly<Record<Zone, Intl.DateTimeFormat>> = {
    rome: new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE.rome }),
    utc: new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE.utc }),
};
const DAY_LABEL: Readonly<Record<Zone, Intl.DateTimeFormat>> = {
    rome: new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE.rome, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
    utc: new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE.utc, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
};
const ROME_DAY = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE.rome, day: '2-digit', month: 'short' });
const SHORT: Readonly<Record<Zone, Intl.DateTimeFormat>> = {
    rome: new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE.rome,
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }),
    utc: new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE.utc,
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }),
};

export const romeTime = (date: Date): string => CLOCK.rome.format(date);
export const utcTime = (date: Date): string => CLOCK.utc.format(date);
export const clockIn = (date: Date, zone: Zone): string => CLOCK[zone].format(date);
export const romeDay = (date: Date): string => ROME_DAY.format(date);
export const zoneLabel = (zone: Zone): string => (zone === 'rome' ? 'Rome' : 'UTC');
export const dayKey = (ms: number, zone: Zone): string => DAY_KEY[zone].format(new Date(ms));
export const dayLabel = (ms: number, zone: Zone): string => DAY_LABEL[zone].format(new Date(ms)).replace(',', '');
// `14 Oct 23:28`: one end of a time range.
export const shortStamp = (ms: number, zone: Zone): string => SHORT[zone].format(new Date(ms)).replace(',', '');

// `2026-10-13T19:12:05.027412Z` → the clock in the zone and the milliseconds.
export function lineTime(ts: string, zone: Zone = 'rome'): { clock: string; ms: string } {
    return { clock: CLOCK[zone].format(new Date(ts)), ms: ts.slice(19, 23) };
}

export const formatCount = (n: number): string => n.toLocaleString('en-US');

// 1234 → 1.2K, for tight places (sparklines, badges).
export function compactCount(n: number): string {
    if (n < 1000) {
        return String(n);
    }
    if (n < 1_000_000) {
        return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
    }

    return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 1 : 0)}M`;
}

// 5_119_952 → 4.9 MB.
export function formatBytes(n: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = n;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }

    return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

// How long ago, in the words of a log reader: 12s, 4m, 3h, 2d.
export function ago(ms: number, now = Date.now()): string {
    const seconds = Math.max(0, Math.round((now - ms) / 1000));
    if (seconds < 60) {
        return `${seconds}s`;
    }
    if (seconds < 3600) {
        return `${Math.floor(seconds / 60)}m`;
    }
    if (seconds < 86400) {
        return `${Math.floor(seconds / 3600)}h`;
    }

    return `${Math.floor(seconds / 86400)}d`;
}

// A level as the list writes it; an `info` line carries no label at all (quiet by default, D-28).
export const LEVEL_LABEL: Readonly<Record<string, string>> = {
    trace: 'trace',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'fatal',
};

// bustard's messages start with their module: `[Cron Job] - CronAuctionsShiftStatesUseCase …`. The list shows the
// module in its own column.
export const messageText = (message: string): string => message.replace(/^\[[^\]]+\] - /, '');

export interface RangePreset {
    key: string;
    label: string;
    short: string;
    ms: number;
}

export const RANGES: readonly RangePreset[] = [
    { key: '5m', label: 'Last 5 minutes', short: '5m', ms: 5 * 60_000 },
    { key: '15m', label: 'Last 15 minutes', short: '15m', ms: 15 * 60_000 },
    { key: '1h', label: 'Last hour', short: '1h', ms: 3600_000 },
    { key: '4h', label: 'Last 4 hours', short: '4h', ms: 4 * 3600_000 },
    { key: '24h', label: 'Last 24 hours', short: '24h', ms: 24 * 3600_000 },
    { key: '7d', label: 'Last 7 days', short: '7d', ms: 7 * 24 * 3600_000 },
    { key: '30d', label: 'Last 30 days', short: '30d', ms: 30 * 24 * 3600_000 },
];

// A span in the words of the range picker: 90 min → 1 h 30 min.
export function spanLabel(ms: number): string {
    if (ms < 60_000) {
        return `${Math.max(1, Math.round(ms / 1000))} s`;
    }
    const minutes = Math.round(ms / 60_000);
    if (minutes < 60) {
        return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
        return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
    }

    return `${Math.round(hours / 24)} days`;
}

// Each environment's name and colour: the edge across the top of the window, its pill, its dot (D-28).
export const ENV_STYLE: Readonly<Record<string, { label: string; color: string }>> = {
    prod: { label: 'Production', color: '#e5484d' },
    preprod: { label: 'Preproduction', color: '#9085e9' },
    demo: { label: 'Demo', color: '#2bc48c' },
    staging: { label: 'Staging', color: '#fab219' },
    local: { label: 'Local', color: '#8497b8' },
};
export const ENV_ORDER = ['prod', 'preprod', 'demo', 'staging', 'local'];
export const envStyle = (env: string): { label: string; color: string } =>
    ENV_STYLE[env] ?? { label: env.charAt(0).toUpperCase() + env.slice(1), color: '#8497b8' };
